import { useState, useEffect } from 'react';
import { getUsers, createUserAPI, updateUserAPI, deleteUserAPI, setUserModulesAPI } from './api';

const ALL_MODULES = [
  { key: 'vsad',           label: 'VSAD Generator',       icon: '📄' },
  { key: 'audit',          label: 'Vessel Audit Mgmt',    icon: '⚓' },
  { key: 'ims',            label: 'QDMS',                 icon: '🗂️' },
  { key: 'qdms-analyzer',  label: 'QDMS Analyzer',        icon: '🧠' },
  { key: 'library',        label: 'Library',              icon: '📚' },
  { key: 'email-assistant',label: 'Email Assistant',      icon: '📧' },
  { key: 'blamecatcher',   label: 'BlameCatcher™',        icon: '🕵️' },
  { key: 'psc-guard',      label: 'PSC Guard',            icon: '⚓' },
  { key: 'brainstorm',     label: 'BrainStorm™',          icon: '🧠' },
  { key: 'report-tracker',   label: 'Report Tracker',    icon: '📅' },
  { key: 'fleet-management', label: 'Fleet Management',  icon: '🚢' },
];

const EMPTY_FORM = { username: '', email: '', full_name: '', password: '', role: 'user' };

export default function UserManagement({ setPage, onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | {user}
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [moduleModal, setModuleModal] = useState(null);  // null | user object
  const [moduleSelection, setModuleSelection] = useState([]);
  const [savingModules, setSavingModules] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const parseModules = (u) => {
    if (!u.allowed_modules) return [];
    try { return JSON.parse(u.allowed_modules); } catch { return []; }
  };

  const openModules = (u) => {
    setModuleSelection(parseModules(u));
    setModuleModal(u);
  };

  const toggleModule = (key) =>
    setModuleSelection(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const saveModules = async () => {
    setSavingModules(true);
    try {
      await setUserModulesAPI(moduleModal.username, moduleSelection);
      setModuleModal(null);
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save modules.'); }
    finally { setSavingModules(false); }
  };

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    } finally { setLoading(false); }
  };

  const openCreate = () => { setForm(EMPTY_FORM); setError(''); setModal('create'); };
  const openEdit = (u) => {
    setForm({ username: u.username, email: u.email, full_name: u.full_name, password: '', role: u.role });
    setError('');
    setModal(u);
  };
  const closeModal = () => setModal(null);

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      if (modal === 'create') {
        if (!form.username || !form.email || !form.password) {
          setError('Username, email and password are required.');
          setSaving(false);
          return;
        }
        await createUserAPI(form);
      } else {
        const payload = { email: form.email, full_name: form.full_name, role: form.role };
        if (form.password) payload.password = form.password;
        await updateUserAPI(modal.username, payload);
      }
      closeModal();
      fetchUsers();
    } catch (e) {
      setError(e.response?.data?.detail || 'An error occurred.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    try {
      await deleteUserAPI(username);
      fetchUsers();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed.');
    }
  };

  const toggleActive = async (u) => {
    try {
      await updateUserAPI(u.username, { is_active: !u.is_active });
      fetchUsers();
    } catch (e) { alert('Update failed.'); }
  };

  const isEditing = modal && modal !== 'create';

  return (
    <div style={s.wrapper}>
      <header style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.logoIcon}>MP</div>
          <div>
            <div style={s.logoTitle}>CSH - QSSE Department</div>
            <div style={s.logoSub}>USER MANAGEMENT</div>
          </div>
        </div>
        <div style={s.headerRight}>
          <button style={s.backBtn} onClick={() => setPage('dashboard')}>← BACK</button>
          <button style={s.logoutBtn} onClick={onLogout}>LOGOUT</button>
        </div>
      </header>

      <main style={s.main}>
        <div style={s.topBar}>
          <div>
            <h1 style={s.pageTitle}>Users</h1>
            <p style={s.pageSub}>{users.length} registered accounts</p>
          </div>
          <button style={s.addBtn} onClick={openCreate}>+ ADD USER</button>
        </div>

        {loading ? (
          <div style={s.loadingText}>Loading users...</div>
        ) : (
          <div style={s.table}>
            <div style={s.tableHeader}>
              <span>USERNAME</span>
              <span>FULL NAME</span>
              <span>EMAIL</span>
              <span>ROLE</span>
              <span>STATUS</span>
              <span>MODULES</span>
              <span>ACTIONS</span>
            </div>
            {users.map(u => {
              const mods = parseModules(u);
              const isAdminUser = u.role === 'admin';
              return (
                <div key={u.id} style={s.tableRow}>
                  <span style={s.mono}>{u.username}</span>
                  <span>{u.full_name || '—'}</span>
                  <span style={s.email}>{u.email}</span>
                  <span>
                    <span style={isAdminUser ? s.badgeAdmin : s.badgeUser}>{u.role}</span>
                  </span>
                  <span>
                    <span
                      style={u.is_active ? s.badgeActive : s.badgeInactive}
                      onClick={() => toggleActive(u)}
                      title="Click to toggle"
                    >
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </span>
                  <span>
                    {isAdminUser
                      ? <span style={s.allAccess}>All access</span>
                      : <span style={s.moduleCount} onClick={() => openModules(u)} title="Click to manage modules">
                          {mods.length}/{ALL_MODULES.length} modules
                        </span>
                    }
                  </span>
                  <span style={s.actions}>
                    <button style={s.editBtn} onClick={() => openEdit(u)}>Edit</button>
                    {!isAdminUser && (
                      <button style={s.modulesBtn} onClick={() => openModules(u)}>Modules</button>
                    )}
                    <button style={s.deleteBtn} onClick={() => handleDelete(u.username)}>Delete</button>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {moduleModal && (
        <div style={s.overlay} onClick={() => setModuleModal(null)}>
          <div style={s.moduleModalBox} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Module Access — {moduleModal.username}</div>
            <div style={s.moduleModalSub}>Select which modules this user can access</div>

            <div style={s.moduleGrid}>
              {ALL_MODULES.map(m => {
                const checked = moduleSelection.includes(m.key);
                return (
                  <div
                    key={m.key}
                    style={{ ...s.moduleItem, ...(checked ? s.moduleItemOn : {}) }}
                    onClick={() => toggleModule(m.key)}
                  >
                    <span style={s.moduleItemIcon}>{m.icon}</span>
                    <span style={s.moduleItemLabel}>{m.label}</span>
                    <div style={{ ...s.moduleCheckbox, ...(checked ? s.moduleCheckboxOn : {}) }}>
                      {checked && '✓'}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={s.moduleQuickRow}>
              <button style={s.quickBtn} onClick={() => setModuleSelection(ALL_MODULES.map(m => m.key))}>Select All</button>
              <button style={s.quickBtn} onClick={() => setModuleSelection([])}>Clear All</button>
            </div>

            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setModuleModal(null)}>Cancel</button>
              <button style={s.saveBtn} onClick={saveModules} disabled={savingModules}>
                {savingModules ? 'Saving…' : `Save (${moduleSelection.length} modules)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>{isEditing ? `Edit: ${modal.username}` : 'New User'}</div>

            {!isEditing && (
              <div style={s.field}>
                <label style={s.label}>Username *</label>
                <input style={s.input} value={form.username}
                  onChange={e => setForm({ ...form, username: e.target.value })} />
              </div>
            )}
            <div style={s.field}>
              <label style={s.label}>Full Name</label>
              <input style={s.input} value={form.full_name}
                onChange={e => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Email *</label>
              <input style={s.input} type="email" value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div style={s.field}>
              <label style={s.label}>{isEditing ? 'New Password (leave blank to keep)' : 'Password *'}</label>
              <input style={s.input} type="password" value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })} />
              <div style={{ fontSize: '11px', color: 'var(--grey)', marginTop: '4px', fontFamily: 'var(--font-mono)' }}>
                Min 8 chars · uppercase · lowercase · number
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Role</label>
              <select style={s.input} value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}>
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
            </div>

            {error && <div style={s.errorMsg}>{error}</div>}

            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={closeModal}>Cancel</button>
              <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrapper: { minHeight: '100vh', background: 'var(--navy)' },
  header: {
    background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)',
    padding: '0 40px', height: '64px', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  logoIcon: {
    width: '36px', height: '36px', background: 'var(--red)', borderRadius: '8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font-mono)', fontSize: '12px', fontWeight: '500', color: 'white',
  },
  logoTitle: { fontSize: '15px', fontWeight: '700' },
  logoSub: { fontSize: '10px', color: 'var(--grey)', letterSpacing: '2px', fontFamily: 'var(--font-mono)' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  backBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 16px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  logoutBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 16px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  main: { padding: '40px', maxWidth: '1200px', margin: '0 auto' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' },
  pageTitle: { fontSize: '28px', fontWeight: '800', color: 'var(--white)' },
  pageSub: { fontSize: '13px', color: 'var(--grey)', marginTop: '4px' },
  addBtn: {
    background: 'var(--red)', border: 'none', borderRadius: '8px',
    padding: '10px 20px', color: 'white', fontSize: '12px',
    fontWeight: '700', letterSpacing: '1px', cursor: 'pointer',
  },
  loadingText: { textAlign: 'center', color: 'var(--grey)', padding: '60px', fontFamily: 'var(--font-mono)' },
  table: { background: 'var(--navy-mid)', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--navy-border)' },
  tableHeader: {
    display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1.8fr 0.8fr 0.8fr 1fr 1.4fr',
    padding: '14px 24px', background: 'rgba(255,255,255,0.03)',
    fontSize: '11px', fontWeight: '600', letterSpacing: '1.5px',
    color: 'var(--grey)', fontFamily: 'var(--font-mono)',
    borderBottom: '1px solid var(--navy-border)',
  },
  tableRow: {
    display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1.8fr 0.8fr 0.8fr 1fr 1.4fr',
    padding: '16px 24px', borderBottom: '1px solid var(--navy-border)',
    fontSize: '13px', alignItems: 'center',
  },
  mono: { fontFamily: 'var(--font-mono)', fontSize: '13px', fontWeight: '600' },
  email: { fontSize: '12px', color: 'var(--grey)', fontFamily: 'var(--font-mono)' },
  badgeAdmin: {
    background: 'rgba(232,0,29,0.15)', border: '1px solid rgba(232,0,29,0.4)',
    color: '#ff6b7a', padding: '3px 10px', borderRadius: '4px',
    fontSize: '11px', fontFamily: 'var(--font-mono)', fontWeight: '600',
  },
  badgeUser: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid var(--navy-border)',
    color: 'var(--grey)', padding: '3px 10px', borderRadius: '4px',
    fontSize: '11px', fontFamily: 'var(--font-mono)',
  },
  badgeActive: {
    background: 'rgba(32,196,112,0.15)', border: '1px solid rgba(32,196,112,0.4)',
    color: '#20c470', padding: '3px 10px', borderRadius: '4px',
    fontSize: '11px', fontFamily: 'var(--font-mono)', cursor: 'pointer',
  },
  badgeInactive: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid var(--navy-border)',
    color: 'var(--grey)', padding: '3px 10px', borderRadius: '4px',
    fontSize: '11px', fontFamily: 'var(--font-mono)', cursor: 'pointer',
  },
  actions: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  editBtn: {
    background: 'var(--gold-dim)', border: '1px solid rgba(201,168,76,0.3)',
    color: 'var(--gold)', padding: '5px 10px', borderRadius: '5px',
    fontSize: '11px', cursor: 'pointer', fontWeight: '600',
  },
  modulesBtn: {
    background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.35)',
    color: '#a78bfa', padding: '5px 10px', borderRadius: '5px',
    fontSize: '11px', cursor: 'pointer', fontWeight: '600',
  },
  deleteBtn: {
    background: 'var(--red-glow)', border: '1px solid rgba(232,0,29,0.3)',
    color: '#ff6b7a', padding: '5px 10px', borderRadius: '5px',
    fontSize: '11px', cursor: 'pointer', fontWeight: '600',
  },
  allAccess: {
    fontSize: '11px', color: '#20c470', fontFamily: 'var(--font-mono)',
  },
  moduleCount: {
    fontSize: '11px', color: '#a78bfa', fontFamily: 'var(--font-mono)',
    cursor: 'pointer', textDecoration: 'underline dotted',
  },
  // Module modal
  moduleModalBox: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '16px', padding: '32px', width: '520px', maxHeight: '80vh', overflowY: 'auto',
  },
  moduleModalSub: { fontSize: '12px', color: 'var(--grey)', marginBottom: '20px', fontFamily: 'var(--font-mono)' },
  moduleGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' },
  moduleItem: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
    border: '1px solid var(--navy-border)', background: 'rgba(255,255,255,0.02)',
    transition: 'all 0.15s',
  },
  moduleItemOn: {
    background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.4)',
  },
  moduleItemIcon: { fontSize: '16px', flexShrink: 0 },
  moduleItemLabel: { fontSize: '12px', flex: 1, fontWeight: '500' },
  moduleCheckbox: {
    width: 18, height: 18, borderRadius: 4, border: '1.5px solid var(--navy-light)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', flexShrink: 0, color: '#a78bfa', fontWeight: '700',
  },
  moduleCheckboxOn: { background: 'rgba(124,58,237,0.3)', borderColor: '#a78bfa' },
  moduleQuickRow: { display: 'flex', gap: '8px', marginBottom: '20px' },
  quickBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '5px 12px', borderRadius: '6px',
    fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modalBox: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '16px', padding: '32px', width: '440px',
  },
  modalTitle: { fontSize: '18px', fontWeight: '800', marginBottom: '24px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '11px', color: 'var(--grey)', letterSpacing: '1px', marginBottom: '6px', fontFamily: 'var(--font-mono)' },
  input: {
    width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-light)',
    borderRadius: '8px', padding: '10px 14px', color: 'var(--white)',
    fontSize: '13px', outline: 'none', boxSizing: 'border-box',
  },
  errorMsg: { color: '#ff6b7a', fontSize: '13px', marginBottom: '16px', fontFamily: 'var(--font-mono)' },
  modalActions: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' },
  cancelBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '10px 20px', borderRadius: '8px',
    fontSize: '13px', cursor: 'pointer',
  },
  saveBtn: {
    background: 'var(--red)', border: 'none', color: 'white',
    padding: '10px 24px', borderRadius: '8px', fontSize: '13px',
    fontWeight: '700', cursor: 'pointer',
  },
};
